import { Component, OnDestroy, OnInit } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Router, ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Actions } from '@src/app/shared/backend/auth/Action';
import { CompanyResponse, GlobalSearchSerialNumberWarrantyRequest, GlobalSearchWarrantyRequest, WarrantiesService, WarrantyGlobalDetailsResponse } from '@src/app/shared/backend/clients';
import { BaseListComponent } from '@src/app/shared/components/base-list/base-list.component';
import { CountryService } from '@src/app/shared/services/countries-service';
import { PermissionService } from '@src/app/shared/services/permission.service';
import { SessionService } from '@src/app/shared/services/session.service';
import { TableSettingService, Column } from '@src/app/shared/services/table.setting.service';
import { firstValueFrom, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { FiltersPanelChangeEvent, FiltersPanelFieldOverrides } from '@src/app/shared/components/filters-panel/filters-panel.models';
import { applyFiltersPanelChange } from '@src/app/shared/components/filters-panel/filters-panel.helper';
import { ActiveInactivePipe } from '@app/shared/pipes/activeInactive.pipe';
import { FriendlyBooleanPipe } from '@app/shared/pipes/friendlyBoolean.pipe';
import { loadStoredPaginatorState, resetPaginatorStateToFirstPage } from '@app/shared/components/paginator/paginator-storage.helper';
import { PageChangeEvent } from '@src/app/shared/components/paginator/custom-paginator.component';

@Component({
    selector: 'app-warranties',
    templateUrl: './warranties.component.html',
    styleUrl: './warranties.component.scss',
    host: {
        class: 'app-warranties'
    },
    standalone: false
})
export class WarrantiesComponent extends BaseListComponent implements OnInit, OnDestroy {

  private readonly componentDestroyed$ = new Subject<void>();
  private initialLoadDone = false;

  public readonly warrantiesTableName = TableSettingService.WarrantiesTableName;
  public readonly warrantiesGlobalSearchTableName = TableSettingService.WarrantiesGlobalSearchTableName;
  public globalSearchTableColumns: Column[] = [];
  public filtersPanelFieldOverrides: FiltersPanelFieldOverrides = {};
  public activeTabIndex: number = 0;

  // Dropdown: isActive
  private statusOptions: Array<{ id: string | number; text: string }> = [];

  // Dropdown isPrepaired
  private friendlyBoolOptions: Array<{ id: string | number; text: string }> = [];

  allowActivateWarranty: boolean = false;
  company: CompanyResponse = {
    inventorySubmissionThresholdDays: 0
  };

  globalSearchFormGroup: FormGroup = this.fb.group({
    searchBy: ['serialNumber', [Validators.required]],
    searchValue: ['', [Validators.required]]
  });
  globalSearchResults: WarrantyGlobalDetailsResponse[] = [];

  isGlobalSearchLoading: boolean = false;
  hasGlobalSearchSubmitted: boolean = false;
  hasGlobalSearchError: boolean = false;

  globalSearchPageNumber: number = 1;
  globalSearchPageSize: number = 10;
  globalSearchTotalRecords: number = 0;

  submittedGlobalSearchBy: string = '';
  submittedGlobalSearchValue: string = '';

  constructor(private router: Router,
    private route: ActivatedRoute,
    private countryService: CountryService,
    public override tableSettingService: TableSettingService,
    private session: SessionService,
    public translateService: TranslateService,
    private warrantiesService: WarrantiesService,
    private permissionService: PermissionService,
    private fb: FormBuilder
  ) {
    super();
  }

  async ngOnInit() {
    if (this.route.snapshot.queryParamMap.get('tab') === 'global-search') {
      this.activeTabIndex = 1;
    }

    await this.init();
  }

  private async init(): Promise<void> {
    this.restorePaginatorStateFromStorage();

    this.company = await this.session.getCompanyData();

    // i18n propagation on lang change
    this.rebuildStatusOptions();
    this.rebuildFriendlyBoolOptions();

    this.translateService.onLangChange
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(() => {
        this.rebuildStatusOptions();
        this.rebuildFriendlyBoolOptions();
        this.rebuildFiltersPanelOverrides();
        this.rebuildSortPanelColumnLabels();
      });

    await this.tableSettingService.init();
    await super.setCache(this.warrantiesTableName);

    const globalSearchColumns = this.tableSettingService.getTableSettings(this.warrantiesGlobalSearchTableName);

    // display same custom columns in warranty list and global search results
    this.globalSearchTableColumns = globalSearchColumns.filter(globalSearchColumn => {
      let warrantyColumnField = globalSearchColumn.field;

      if (globalSearchColumn.field === 'serialNumber') {
        warrantyColumnField = 'itemSerialNumber';
      }

      const isVisibleInWarrantiesTab = this.tableColumns.some(
        (warrantyColumn: Column) => warrantyColumn.field === warrantyColumnField
      );

      return isVisibleInWarrantiesTab;
    });

    this.initSortPanel({
      // display recently updated 1st
      defaultSort: { key: 'updatedOn', direction: 'DESC' },
      labelResolver: (col) => {
        const translationKey = `TABLE.HEADER.${col.header}`;
        const translated = this.translateService.instant(translationKey);

        if (translated && translated !== translationKey) {
          return translated;
        }

        return col.header;
      }
    });

    this.allowActivateWarranty = await this.permissionService.hasActionPermissions(Actions.activate_warranties);

    this.rebuildSortPanelColumnLabels();
    this.rebuildFiltersPanelOverrides();

    // Fallback: if the panel doesn't restore anything, load unfiltered!!
    setTimeout(async () => {
      if (!this.initialLoadDone) {
        this.initialLoadDone = true;
        await this.get();
      }
    });
  }

  override async get() {
    this.filter.pageNumber = this.pageNumber;
    this.filter.pageSize = this.pageSize;

    this.applySortToFilterRequest();

    this.countries = await this.countryService.getCountries();

    this.page = await firstValueFrom(this.warrantiesService.apiWarrantiesFilterTablePost(this.filter));

    this.page.results?.forEach((v: any) => {
      v.isPrepaired = this.translateService.instant('ENUMS.' + v.isPrepaired.toString().toUpperCase());
    });

    this.data = this.page.results;
  }

  override onPageChange(event: any): void {
    this.initialLoadDone = true;
    super.onPageChange(event);
  }

  async onGlobalSearchPageChange(event: PageChangeEvent): Promise<void> {
    if (this.isGlobalSearchLoading) {
      return;
    }

    if (this.submittedGlobalSearchBy !== 'serialNumber') {
      return;
    }

    this.globalSearchPageNumber = event.page;
    this.globalSearchPageSize = event.rows;

    this.globalSearchResults = [];
    this.hasGlobalSearchError = false;
    this.isGlobalSearchLoading = true;
    this.globalSearchFormGroup.disable({ emitEvent: false });

    try {
      const payload = {
        serialNumber: this.submittedGlobalSearchValue,
        pageNumber: this.globalSearchPageNumber,
        pageSize: this.globalSearchPageSize
      };

      const response = await firstValueFrom(this.warrantiesService.apiWarrantiesSearchSerialNumberPost(payload));

      this.globalSearchResults = response.results ?? [];
      this.globalSearchTotalRecords = response.totalCount ?? 0;
    } catch (error) {
      this.globalSearchResults = [];
      this.globalSearchTotalRecords = 0;

      if (error instanceof HttpErrorResponse && error.error?.code === '40401') {
        this.hasGlobalSearchError = false;
      } else {
        this.hasGlobalSearchError = true;
        console.error('Error loading warranty search results:', error);
      }
    } finally {
      this.globalSearchFormGroup.enable({ emitEvent: false });
      this.isGlobalSearchLoading = false;
    }
  }

  protected override onCriteriaResetRequested(): void {
    resetPaginatorStateToFirstPage({
      tableName: this.warrantiesTableName,
      pageSize: this.pageSize,
    });
  }

  private restorePaginatorStateFromStorage(): void {
    const stored = loadStoredPaginatorState(this.warrantiesTableName);

    if (!stored) {
      return;
    }

    const storedPageNumber = stored.valuesByFieldKey.pageNumber;
    const storedPageSize = stored.valuesByFieldKey.pageSize;

    this.pageNumber = storedPageNumber;
    this.pageSize = storedPageSize;

    this.filter.pageNumber = storedPageNumber;
    this.filter.pageSize = storedPageSize;
  }

  private rebuildFiltersPanelOverrides() {
    this.filtersPanelFieldOverrides = {
      isActive: {
        fieldLabel: 'TABLE.HEADER.Status',
        isMultiselect: true,
        options: this.statusOptions ?? [],
        optionLabel: 'text',
        optionValue: 'id',
        searchMode: 'local',
        showSearchbox: false,
        showClear: true,
        compactUi: true,
        pageSize: 10
      },

      isPrepaired: {
        fieldLabel: 'TABLE.HEADER.Prepaired',
        isMultiselect: true,
        options: this.friendlyBoolOptions ?? [],
        optionLabel: 'text',
        optionValue: 'id',
        searchMode: 'local',
        showSearchbox: false,
        showClear: true,
        compactUi: true
      },

      hasD2C: {
        fieldLabel: 'TABLE.HEADER.HasD2C',
        isMultiselect: true,
        options: this.friendlyBoolOptions ?? [],
        optionLabel: 'text',
        optionValue: 'id',
        searchMode: 'local',
        showSearchbox: false,
        showClear: true,
        compactUi: true
      },
    };
  }

  async onFiltersPanelChange(evt: FiltersPanelChangeEvent): Promise<void> {
    const currentFilters = this.filter.filters as any;

    const nextFilters = applyFiltersPanelChange(
      evt,
      currentFilters,
      this.allTableColumns
    );

    if (!nextFilters) {
      return;
    }

    this.filter.filters = nextFilters as any;

    const isApplyAction = evt.source === 'apply';
    const isClearAction = evt.source === 'clear';
    const shouldResetPaging = isApplyAction || isClearAction;

    if (shouldResetPaging) {
      const firstPageNumber = 1;

      this.pageNumber = firstPageNumber;
      this.filter.pageNumber = firstPageNumber;

      this.onCriteriaResetRequested();
    }

    this.initialLoadDone = true;
    await this.get();
  }

  private rebuildStatusOptions() {
    const pipe = new ActiveInactivePipe(this.translateService);
    this.statusOptions = pipe.values;
  }

  private rebuildFriendlyBoolOptions() {
    const pipe = new FriendlyBooleanPipe(this.translateService);
    this.friendlyBoolOptions = pipe.values;
  }

  private rebuildSortPanelColumnLabels(): void {
    const visibleColumns = Array.isArray(this.tableColumns) ? this.tableColumns : [];

    const nextSortableColumns = visibleColumns
      .filter((col: any) => {
        const field = String(col?.field ?? '').trim();
        return (
          field.length > 0 &&
          field.toLowerCase() !== 'actions' &&
          col?.sortable !== false
        );
      })
      .map((col: any) => {
        const translationKey = `TABLE.HEADER.${col.header}`;
        const translated = this.translateService.instant(translationKey);

        return {
          key: col.field,
          label: translated && translated !== translationKey ? translated : col.header
        };
      });

    this.sortableColumns = nextSortableColumns;

    // clear active sort if points to col that is not available due to system settings update
    const allowedKeys = new Set(nextSortableColumns.map(col => col.key));
    if (this.sortPanelSort?.key && !allowedKeys.has(this.sortPanelSort.key)) {
      this.onSortPanelChange(null);
    }
  }

  goToActivateWarrantyForm(): void {
    if (this.company.implicitSaleCreation) {
      this.router.navigate(['/sales/add']);
    } else {
      this.router.navigate(['/warranties/activate']);
    }
  }

  goToWarrantyDetails(event: any, warrantyId: string) {
    this.router.navigate([`/warranties/${warrantyId}`]);
  }

  goToGlobalWarrantyDetails(warranty: WarrantyGlobalDetailsResponse): void {
    // pass data to detail view storing it in the browser history through navigation state
    this.router.navigate(['/warranties/global-search'], {
      state: {
        warranty: warranty
      }
    });
  }

  onQRCodeScanned(qrCode: string): void {
    this.globalSearchFormGroup.patchValue({
      searchValue: qrCode
    });
  }

  async onGlobalSearchSubmit(): Promise<void> {
    if (this.isGlobalSearchLoading) {
      return;
    }

    const searchValue = this.globalSearchFormGroup.controls['searchValue'].value?.trim();

    if (!searchValue) {
      this.globalSearchFormGroup.controls['searchValue'].setValue('');
      this.globalSearchFormGroup.markAllAsTouched();
      return;
    }

    if (this.globalSearchFormGroup.invalid) {
      this.globalSearchFormGroup.markAllAsTouched();
      return;
    }

    this.globalSearchFormGroup.controls['searchValue'].setValue(searchValue);

    this.submittedGlobalSearchBy = this.globalSearchFormGroup.controls['searchBy'].value;
    this.submittedGlobalSearchValue = searchValue;

    this.globalSearchPageNumber = 1;
    this.globalSearchTotalRecords = 0;
    this.globalSearchResults = [];

    this.hasGlobalSearchSubmitted = true;
    this.hasGlobalSearchError = false;
    this.isGlobalSearchLoading = true;

    this.globalSearchFormGroup.disable({ emitEvent: false });

    try {
      if (this.submittedGlobalSearchBy === 'serialNumber') {

        const payload : GlobalSearchSerialNumberWarrantyRequest = {
          serialNumber: this.submittedGlobalSearchValue,
          pageNumber: this.globalSearchPageNumber,
          pageSize: this.globalSearchPageSize
        };

        const response = await firstValueFrom(this.warrantiesService.apiWarrantiesSearchSerialNumberPost(payload));

        this.globalSearchResults = response.results ?? [];
        this.globalSearchTotalRecords = response.totalCount ?? 0;
      } else {
        const payload : GlobalSearchWarrantyRequest = {
          code: this.submittedGlobalSearchValue,
          pageNumber: this.globalSearchPageNumber,
          pageSize: this.globalSearchPageSize
        };

        const response = await firstValueFrom(this.warrantiesService.apiWarrantiesSearchCodePost(payload));

        if (response) {
          this.globalSearchResults = response.results ?? [];
          this.globalSearchTotalRecords = response.totalCount ?? 0;
        }
      }
    } catch(error) {
      this.globalSearchResults = [];
      this.globalSearchTotalRecords = 0;

      // 40401: no warranties found
      if (error instanceof HttpErrorResponse && error.error?.code === '40401') {
        this.hasGlobalSearchError = false;
      } else {
        this.hasGlobalSearchError = true;
        console.error('Error searching warranties:', error);
      }
    } finally {
      this.globalSearchFormGroup.enable({ emitEvent: false });
      this.isGlobalSearchLoading = false;
    }
  }

  ngOnDestroy() {
    this.componentDestroyed$.next();
    this.componentDestroyed$.complete();
  }
}
